import { supabase } from "@/platform/lib/supabase";

export interface SimulationQuestion {
  id: string;
  number: number | null;
  kind: "objective" | "discursive";
  stem: string;
  alternatives: Array<{ label: string; text: string }> | null;
  correct_answer: string | null;
  has_base_text: boolean;
  base_text: string | null;
  discipline: string;
  exam_year: number;
}

export interface SimulationSession {
  id: string;
  user_id: string;
  started_at: string;
  question_ids: string[];
  /** Quantidade de objetivas no início do array. Discursivas vêm depois. */
  objectives_count: number;
}

export interface SimulationSummary {
  total: number;
  answered: number;
  objectives_correct: number;
  objectives_total: number;
  discursives_total: number;
  by_discipline: Record<string, { total: number; correct: number }>;
  duration_seconds: number;
  raw_score: number;
}

const TARGET_OBJECTIVES = 50;
const TARGET_DISCURSIVES = 5;

interface RawQuestion {
  id: string;
  number: number | null;
  kind: "objective" | "discursive" | "essay";
  stem: string;
  has_base_text: boolean;
  base_text: string | null;
  alternatives: Array<{ label: string; text: string }> | null;
  correct_answer: string | null;
  discipline: string;
  einstein_exam_papers: { year: number } | null;
}

function toSimQuestion(r: RawQuestion): SimulationQuestion {
  return {
    id: r.id,
    number: r.number,
    kind: r.kind === "essay" ? "discursive" : r.kind,
    stem: r.stem,
    alternatives: r.alternatives,
    correct_answer: r.correct_answer,
    has_base_text: r.has_base_text,
    base_text: r.base_text,
    discipline: r.discipline,
    exam_year: r.einstein_exam_papers?.year ?? 0,
  };
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = a[i];
    const o = a[j];
    if (tmp !== undefined && o !== undefined) {
      a[i] = o;
      a[j] = tmp;
    }
  }
  return a;
}

/**
 * Picks ~50 objetivas balanceadas por disciplina + ~5 discursivas (uma de cada
 * disciplina que cai em discursiva). Replica composição da prova Einstein:
 * 50 obj distribuídas e 5 disc (Bio, Quím, Fís, Mat, Inglês).
 */
export async function startSimulationSession(userId: string): Promise<{
  session: SimulationSession;
  questions: SimulationQuestion[];
}> {
  // Pool objetivas
  const { data: objPool, error: objErr } = await supabase
    .from("einstein_questions")
    .select(
      "id, number, kind, stem, has_base_text, base_text, alternatives, correct_answer, discipline, einstein_exam_papers!inner(year)",
    )
    .eq("kind", "objective")
    .neq("discipline", "unknown")
    .not("correct_answer", "is", null)
    .neq("correct_answer", "ANNULLED")
    .limit(400);
  if (objErr || !objPool) throw new Error(`load obj pool: ${objErr?.message}`);

  // Pool discursivas (Bio/Quím/Fís/Mat/Inglês)
  const { data: discPool, error: discErr } = await supabase
    .from("einstein_questions")
    .select(
      "id, number, kind, stem, has_base_text, base_text, alternatives, correct_answer, discipline, einstein_exam_papers!inner(year)",
    )
    .eq("kind", "discursive")
    .in("discipline", ["biologia", "quimica", "fisica", "matematica", "ingles"])
    .limit(100);
  if (discErr || !discPool) throw new Error(`load disc pool: ${discErr?.message}`);

  // Compor objetivas balanceadas
  const objBuckets: Record<string, RawQuestion[]> = {};
  for (const q of objPool as unknown as RawQuestion[]) {
    const arr = objBuckets[q.discipline] ?? [];
    arr.push(q);
    objBuckets[q.discipline] = arr;
  }
  for (const k of Object.keys(objBuckets)) {
    objBuckets[k] = shuffle(objBuckets[k]!);
  }
  const objectiveQuestions: SimulationQuestion[] = [];
  const disciplines = Object.keys(objBuckets);
  let round = 0;
  while (objectiveQuestions.length < TARGET_OBJECTIVES && disciplines.length > 0) {
    let progress = false;
    for (const d of disciplines) {
      const bucket = objBuckets[d]!;
      if (bucket[round]) {
        objectiveQuestions.push(toSimQuestion(bucket[round]!));
        progress = true;
        if (objectiveQuestions.length >= TARGET_OBJECTIVES) break;
      }
    }
    if (!progress) break;
    round += 1;
  }

  // Compor discursivas (1 de cada das 5 disciplinas se possível)
  const discBuckets: Record<string, RawQuestion[]> = {};
  for (const q of discPool as unknown as RawQuestion[]) {
    const arr = discBuckets[q.discipline] ?? [];
    arr.push(q);
    discBuckets[q.discipline] = arr;
  }
  const discursiveQuestions: SimulationQuestion[] = [];
  for (const d of ["biologia", "quimica", "fisica", "matematica", "ingles"]) {
    const bucket = discBuckets[d];
    if (bucket && bucket.length > 0) {
      const pick = shuffle(bucket)[0]!;
      discursiveQuestions.push(toSimQuestion(pick));
    }
    if (discursiveQuestions.length >= TARGET_DISCURSIVES) break;
  }

  const questions = [...objectiveQuestions, ...discursiveQuestions];
  const objectivesCount = objectiveQuestions.length;

  const { data: sessionRow, error: sessErr } = await supabase
    .from("einstein_sessions")
    .insert({
      user_id: userId,
      kind: "simulation_full",
      started_at: new Date().toISOString(),
      meta: {
        question_ids: questions.map((q) => q.id),
        objectives_count: objectivesCount,
      },
    })
    .select("id, user_id, started_at")
    .single();
  if (sessErr || !sessionRow) throw new Error(`create session: ${sessErr?.message}`);

  return {
    session: {
      id: sessionRow.id as string,
      user_id: sessionRow.user_id as string,
      started_at: sessionRow.started_at as string,
      question_ids: questions.map((q) => q.id),
      objectives_count: objectivesCount,
    },
    questions,
  };
}

export async function recordSimulationAttempt(params: {
  userId: string;
  sessionId: string;
  questionId: string;
  answer: string;
  correctAnswer: string | null;
  kind: "objective" | "discursive";
  timeSpentMs: number;
}): Promise<void> {
  // Objetiva pontua 1 se correta, 0 se errada. Discursiva: 0..6 (não corrigimos
  // no simulado em si — usuário pode mandar pra correção depois).
  const isCorrect = params.kind === "objective" && params.correctAnswer
    ? params.answer === params.correctAnswer
    : null;

  // Sem unique constraint global em (session_id, question_id) — discursivas
  // avulsas precisam de múltiplos attempts. Por isso checamos manualmente
  // antes para decidir entre insert e update (resolve o bug de "trocar de
  // alternativa duplica linha" sem migration).
  const { data: existing } = await supabase
    .from("einstein_attempts")
    .select("id")
    .eq("session_id", params.sessionId)
    .eq("question_id", params.questionId)
    .maybeSingle();

  const row = {
    answer: params.answer.slice(0, 8000),
    is_correct: isCorrect,
    score: isCorrect === true ? 1 : 0,
    time_spent_ms: params.timeSpentMs,
  };

  if (existing?.id) {
    const { error } = await supabase
      .from("einstein_attempts")
      .update(row)
      .eq("id", existing.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from("einstein_attempts").insert({
      ...row,
      user_id: params.userId,
      session_id: params.sessionId,
      question_id: params.questionId,
    });
    if (error) throw error;
  }
}

/**
 * Busca uma sessão de simulado em aberto (sem ended_at) do usuário e devolve
 * tudo necessário para retomar: questions, started_at, answers já gravados.
 * Retorna null se não houver sessão aberta — caller chama startSimulationSession.
 *
 * Sem isto, refresh / fechamento de aba em sessão de 5h faz a aluna perder
 * todo o progresso visual (a sessão no banco continua aberta como zumbi).
 */
export async function loadOpenSimulationSession(userId: string): Promise<{
  session: SimulationSession;
  questions: SimulationQuestion[];
  answers: Record<string, string>;
} | null> {
  const { data: sess } = await supabase
    .from("einstein_sessions")
    .select("id, user_id, started_at, meta")
    .eq("user_id", userId)
    .eq("kind", "simulation_full")
    .is("ended_at", null)
    .order("started_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (!sess) return null;

  const meta = sess.meta as
    | { question_ids?: string[]; objectives_count?: number }
    | null;
  const questionIds = meta?.question_ids ?? [];
  if (questionIds.length === 0) return null;

  const { data: qs, error: qErr } = await supabase
    .from("einstein_questions")
    .select(
      "id, number, kind, stem, has_base_text, base_text, alternatives, correct_answer, discipline, einstein_exam_papers!inner(year)",
    )
    .in("id", questionIds);
  if (qErr || !qs) throw new Error(`load questions: ${qErr?.message}`);

  // Preserva ordem da sessão (qs do Supabase volta desordenado).
  const byId = new Map<string, RawQuestion>(
    (qs as unknown as RawQuestion[]).map((q) => [q.id, q]),
  );
  const questions: SimulationQuestion[] = questionIds
    .map((id) => byId.get(id))
    .filter((q): q is RawQuestion => Boolean(q))
    .map(toSimQuestion);

  const { data: attempts } = await supabase
    .from("einstein_attempts")
    .select("question_id, answer")
    .eq("session_id", sess.id);
  const answers: Record<string, string> = {};
  for (const a of (attempts ?? []) as Array<{ question_id: string; answer: string | null }>) {
    if (a.answer) answers[a.question_id] = a.answer;
  }

  return {
    session: {
      id: sess.id as string,
      user_id: sess.user_id as string,
      started_at: sess.started_at as string,
      question_ids: questionIds,
      objectives_count: meta?.objectives_count ?? 0,
    },
    questions,
    answers,
  };
}

export async function endSimulationSession(
  sessionId: string,
  summary: { rawScore: number; durationSeconds: number; byDiscipline: Record<string, { total: number; correct: number }> },
): Promise<void> {
  const { error } = await supabase
    .from("einstein_sessions")
    .update({
      ended_at: new Date().toISOString(),
      duration_seconds: summary.durationSeconds,
      raw_score: summary.rawScore,
      meta: { by_discipline: summary.byDiscipline },
    })
    .eq("id", sessionId);
  if (error) throw error;
}

export async function computeSimulationSummary(sessionId: string): Promise<SimulationSummary> {
  const { data: attempts } = await supabase
    .from("einstein_attempts")
    .select("question_id, is_correct, time_spent_ms")
    .eq("session_id", sessionId);
  const { data: sess } = await supabase
    .from("einstein_sessions")
    .select("started_at, meta")
    .eq("id", sessionId)
    .single();

  const questionIds = (sess?.meta as { question_ids?: string[] } | null)?.question_ids ?? [];
  const objCount = (sess?.meta as { objectives_count?: number } | null)?.objectives_count ?? 0;

  const { data: qs } = await supabase
    .from("einstein_questions")
    .select("id, kind, discipline")
    .in("id", questionIds);
  const qById = new Map<string, { kind: string; discipline: string }>(
    ((qs ?? []) as Array<{ id: string; kind: string; discipline: string }>).map((q) => [
      q.id,
      { kind: q.kind, discipline: q.discipline },
    ]),
  );

  const byDisc: Record<string, { total: number; correct: number }> = {};
  let objCorrect = 0;
  let totalTimeMs = 0;
  let answered = 0;

  for (const a of (attempts ?? []) as Array<{
    question_id: string;
    is_correct: boolean | null;
    time_spent_ms: number | null;
  }>) {
    answered += 1;
    totalTimeMs += a.time_spent_ms ?? 0;
    const meta = qById.get(a.question_id);
    if (!meta) continue;
    const bucket = byDisc[meta.discipline] ?? { total: 0, correct: 0 };
    bucket.total += 1;
    if (a.is_correct === true) {
      bucket.correct += 1;
      if (meta.kind === "objective") objCorrect += 1;
    }
    byDisc[meta.discipline] = bucket;
  }

  const totalSec = sess?.started_at
    ? Math.floor((Date.now() - new Date(sess.started_at).getTime()) / 1000)
    : Math.floor(totalTimeMs / 1000);

  return {
    total: questionIds.length,
    answered,
    objectives_correct: objCorrect,
    objectives_total: objCount,
    discursives_total: questionIds.length - objCount,
    by_discipline: byDisc,
    duration_seconds: totalSec > 0 ? totalSec : Math.floor(totalTimeMs / 1000),
    raw_score: objCorrect, // pontuação bruta = objetivas certas (discursivas precisam correção LLM separada)
  };
}
